import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { ThemeService } from './theme.service';

export type SetupStepKey = 'municipio' | 'direcciones' | 'equipo' | 'catalogos';

export interface SetupProgress {
  municipio: boolean;
  direcciones: boolean;
  equipo: boolean;
  catalogos: boolean;
}

export interface MunicipioConfig {
  logoDataUrl: string;
  primaryColor: string;
  municipalityName: string;
  siteUrl: string;
  timezone: string;
  phone: string;
  email: string;
  address: string;
  mapCenterLat: number;
  mapCenterLng: number;
  rfc: string;
  legalName: string;
  fiscalAddress: string;
  privacyNotice: string;
  termsAndConditions: string;
}

const CATALOG = 'SystemConfiguration';
const KEY_PROGRESS = 'session_setup';
const KEY_MUNICIPIO = 'municipio_config';
const KEY_MAP = 'general_config';

const EMPTY_PROGRESS: SetupProgress = {
  municipio: false,
  direcciones: false,
  equipo: false,
  catalogos: false
};

@Injectable({ providedIn: 'root' })
export class SessionSetupService {

  constructor(
    private api: ApiService,
    private theme: ThemeService
  ) {}

  emptyMunicipio(): MunicipioConfig {
    return {
      logoDataUrl: '',
      primaryColor: this.theme.brand.primary || '#18181b',
      municipalityName: this.theme.brand.municipality || '',
      siteUrl: '',
      timezone: 'America/Mexico_City',
      phone: this.theme.brand.contact.phone || '',
      email: this.theme.brand.contact.email || '',
      address: this.theme.brand.contact.address || '',
      mapCenterLat: 19.1139,
      mapCenterLng: -96.1205,
      rfc: '',
      legalName: '',
      fiscalAddress: '',
      privacyNotice: '',
      termsAndConditions: ''
    };
  }

  async loadProgress(): Promise<SetupProgress> {
    const row = await this.findByKey(KEY_PROGRESS);
    if (!row || !row.ValueBeta) {
      return { ...EMPTY_PROGRESS };
    }
    try {
      return { ...EMPTY_PROGRESS, ...JSON.parse(row.ValueBeta) };
    } catch {
      return { ...EMPTY_PROGRESS };
    }
  }

  async markComplete(step: SetupStepKey): Promise<SetupProgress> {
    const progress = await this.loadProgress();
    progress[step] = true;
    const row = await this.findByKey(KEY_PROGRESS);
    const body = {
      ValueAlpha: KEY_PROGRESS,
      ValueBeta: JSON.stringify(progress),
      Status: 'active',
      Description: 'Progreso de personalización de sesión'
    };
    await this.upsert(row, body);
    return progress;
  }

  async loadMunicipio(): Promise<MunicipioConfig> {
    const base = this.emptyMunicipio();
    const row = await this.findByKey(KEY_MUNICIPIO);
    if (!row) {
      return base;
    }
    try {
      const general = row.ValueGamma ? JSON.parse(row.ValueGamma) : {};
      const legal = row.ValueEpsilon ? JSON.parse(row.ValueEpsilon) : {};
      return { ...base, ...general, ...legal };
    } catch {
      return base;
    }
  }

  async saveMunicipio(data: MunicipioConfig, markStep = true): Promise<void> {
    const row = await this.findByKey(KEY_MUNICIPIO);
    const general = {
      logoDataUrl: data.logoDataUrl || '',
      primaryColor: data.primaryColor,
      municipalityName: data.municipalityName,
      siteUrl: data.siteUrl,
      timezone: data.timezone,
      phone: data.phone,
      email: data.email,
      address: data.address,
      mapCenterLat: data.mapCenterLat,
      mapCenterLng: data.mapCenterLng,
      rfc: data.rfc,
      legalName: data.legalName,
      fiscalAddress: data.fiscalAddress
    };
    const legal = {
      privacyNotice: data.privacyNotice || '',
      termsAndConditions: data.termsAndConditions || ''
    };
    const body = {
      ValueAlpha: KEY_MUNICIPIO,
      ValueBeta: (data.municipalityName || '').slice(0, 480),
      ValueGamma: JSON.stringify(general),
      ValueEpsilon: JSON.stringify(legal),
      Status: 'active',
      Description: 'Datos del municipio (personalización)'
    };
    await this.upsert(row, body);
    await this.syncMapConfig(data);
    this.theme.applyMunicipio({
      name: data.municipalityName,
      municipality: data.municipalityName,
      logo: data.logoDataUrl,
      primary: data.primaryColor,
      address: data.address,
      phone: data.phone,
      email: data.email
    });
    if (markStep && this.isMunicipioCaptured(data)) {
      await this.markComplete('municipio');
    }
  }

  isMunicipioCaptured(data: MunicipioConfig): boolean {
    return !!(data.municipalityName && data.email && data.timezone);
  }

  async hydrateTheme(): Promise<void> {
    const data = await this.loadMunicipio();
    if (!data.municipalityName && !data.primaryColor && !data.logoDataUrl) {
      return;
    }
    this.theme.applyMunicipio({
      name: data.municipalityName || this.theme.brand.name,
      municipality: data.municipalityName || this.theme.brand.municipality,
      logo: data.logoDataUrl,
      primary: data.primaryColor || this.theme.brand.primary,
      address: data.address,
      phone: data.phone,
      email: data.email
    });
  }

  private async syncMapConfig(data: MunicipioConfig): Promise<void> {
    const row = await this.findByKey(KEY_MAP);
    const payload = {
      mapCenterLat: data.mapCenterLat,
      mapCenterLng: data.mapCenterLng,
      cityName: data.address || '',
      timezone: data.timezone
    };
    await this.upsert(row, {
      ValueAlpha: KEY_MAP,
      ValueBeta: JSON.stringify(payload),
      Status: 'active',
      Description: 'Configuración general del sistema'
    });
  }

  private async findByKey(key: string): Promise<any | null> {
    const result = await this.api.getCatalogInfo(CATALOG, 100, 0).toPromise();
    const list = result && (result.list || result);
    const arr = Array.isArray(list) ? list : ((list && list.list) || []);
    return arr.find((item: any) => item.ValueAlpha === key) || null;
  }

  private async upsert(row: any, body: any): Promise<void> {
    if (row && row.id) {
      await this.api.updateCatalogInfo(CATALOG, row.id, body).toPromise();
    } else {
      await this.api.createCatalogInfo(CATALOG, body).toPromise();
    }
  }
}
